import { graphql } from '@/__generated__';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { useQueryStable } from '../useQueryStable';
import { GetDefinitionNode } from './testUtil';

type Result = ReturnType<typeof useMacroByEnvironment>[0];

export type MacroResponse = NonNullable<Result['data']>;

export type RequestedMacroDefinitionNode = GetDefinitionNode<'macros', MacroResponse>;

export type MacroArgument = RequestedMacroDefinitionNode['arguments'][number];

// https://github.com/dbt-labs/codex-api/blob/main/schema.graphql
// https://github.com/dbt-labs/codex-api/blob/main/frontend/package.json#L33
// https://github.com/dbt-labs/codex-api/blob/main/frontend/codegen.yml
// generates: https://github.com/dbt-labs/codex-api/blob/main/frontend/src/typings/codex.ts
export const GetMacroByUniqueId = graphql(/* GraphQL */ `
  query Macro($environmentId: BigInt!, $uniqueId: String!, $userId: BigInt) {
    environment(id: $environmentId, userId: $userId) {
      definition {
        lastUpdatedAt
        macros(first: 1, filter: { uniqueIds: [$uniqueId] }) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              uniqueId
              tags
              description
              meta
              name
              packageName
              filePath
              patchPath
              macroSql
              runId
              projectId
              arguments {
                name
                description
                type
              }
            }
          }
        }
      }
    }
  }
`);

type MacroByEnvironmentParams = {
  environmentId: number | undefined;
  uniqueId: string;
  userId: number | null;
  pause?: boolean;
};

/**
 * Macros are Definition Only
 */
export const useMacroByEnvironment = ({
  environmentId = 0,
  uniqueId,
  userId,
  pause,
}: MacroByEnvironmentParams) => {
  const query = useQueryStable({
    query: GetMacroByUniqueId,
    variables: { environmentId, uniqueId, userId: userId ?? null },
    pause: pause || !environmentId || !uniqueId,
  });

  useCacheMonitor({ definition: query[0].data?.environment.definition?.lastUpdatedAt });

  return query;
};
